import { kebabCase, startCase } from 'lodash';

import {
  CheckMetadata,
  CheckMetadataItemType,
  GroupedCheckMetadataItem,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import { displayIdNumber } from 'dashboard/components/ChecksTable/computeCheckMetadata/utils';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { IdExtractedPropertiesDetection, RequiredProperty } from 'dashboard/models/check';
import Sticker from 'design-system/components/Sticker/Sticker';

export const computeIdExtractedPropertiesDetectionMetadata = (
  check: IdExtractedPropertiesDetection,
  hasPermissionVerificationGovIdNumberView: boolean,
): CheckMetadata => {
  const requiredProperties = check.metadata?.['required-properties'] ?? [];
  const absentProperties = check.metadata?.['absent-properties'] ?? [];
  const absentFields = absentProperties.map((property) => property.field);

  const requiredPropertyItems = [];
  const optionalPropertyItems = [];

  for (const requiredProperty of Object.values(RequiredProperty)) {
    // Note: the metadata's required-properties are in snake case.
    const propertyRequired = requiredProperties.includes(requiredProperty);
    const propertyDisplayName = startCase(requiredProperty);

    const propertyValue = check.metadata?.[
      kebabCase(requiredProperty) as keyof typeof check.metadata
    ] as string;
    let propertyDisplayValue;
    if (
      [
        RequiredProperty.Birthdate,
        RequiredProperty.IssueDate,
        RequiredProperty.ExpirationDate,
      ].includes(requiredProperty)
    ) {
      propertyDisplayValue = formatDate(propertyValue);
    } else if (requiredProperty === RequiredProperty.IdentificationNumber) {
      propertyDisplayValue = displayIdNumber(
        propertyValue,
        hasPermissionVerificationGovIdNumberView,
      );
    } else {
      propertyDisplayValue = propertyValue;
    }
    if (propertyRequired) {
      if (propertyValue) {
        requiredPropertyItems.push({
          label: propertyDisplayName,
          value: propertyDisplayValue,
        });
      } else if (absentFields.includes(requiredProperty as RequiredProperty)) {
        requiredPropertyItems.push({
          label: propertyDisplayName,
          value: <Sticker variant='caution'>Absent on ID</Sticker>,
        });
      } else {
        requiredPropertyItems.push({
          label: propertyDisplayName,
          value: <Sticker variant='danger'>Not Extracted</Sticker>,
        });
      }
    } else if (propertyValue) {
      optionalPropertyItems.push({
        label: propertyDisplayName,
        value: propertyDisplayValue,
      });
    }
  }

  // Display all required properties before any non-required properties
  const metadata = [];
  if (requiredPropertyItems.length > 0) {
    metadata.push({
      type: CheckMetadataItemType.Group,
      heading: {
        title: 'Required Properties',
      },
      items: requiredPropertyItems,
    } as GroupedCheckMetadataItem);
  }

  if (optionalPropertyItems.length > 0) {
    metadata.push({
      type: CheckMetadataItemType.Group,
      heading: {
        title: 'Optional Properties',
      },
      items: optionalPropertyItems,
    } as GroupedCheckMetadataItem);
  }

  return {
    metadata: metadata,
    hideIfVerificationPassed: false,
  };
};
